import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import ActiveAnnouncementBanner from "components/announcement/ActiveAnnouncementBanner";
import RoutesConfig from "routes";

export default function AdminLayout(props) {
  const routes = RoutesConfig();
  const location = useLocation();
  const [open, setOpen] = React.useState(window.innerWidth >= 1200);
  const [currentRoute, setCurrentRoute] = React.useState("");

  // Flatten admin routes (parents + children) for easy matching
  const flatAdminRoutes = React.useMemo(() => {
    const result = [];
    routes
      .filter((r) => r.layout === "/admin")
      .forEach((r) => {
        result.push(r);
        if (Array.isArray(r.children)) {
          r.children.forEach((c) => result.push({ ...c, __parent: r }));
        }
      });
    return result;
  }, []);

  // Update sidebar on window resize
  React.useEffect(() => {
    const handleResize = () => setOpen(window.innerWidth >= 1200);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Helper: find active route (works for children too)
  const getActiveRouteObj = React.useCallback(() => {
    const sortedRoutes = [...flatAdminRoutes].sort(
      (a, b) => b.path.length - a.path.length
    );

    return sortedRoutes.find((r) => location.pathname.startsWith("/" + r.path));
  }, [flatAdminRoutes, location.pathname]);

  // Update current route when location changes
  React.useEffect(() => {
    const active = getActiveRouteObj();
    if (active) {
      setCurrentRoute(active.path || "");
    } else {
      setCurrentRoute("");
    }
  }, [location.pathname, getActiveRouteObj]);

  // Navbar "secondary" flag (inherit from parent if child)
  const getActiveNavbar = () => {
    const active = getActiveRouteObj();
    if (!active) return false;
    // if child, allow parent.secondary to drive the look too
    return Boolean(active.secondary || active.__parent?.secondary);
  };

  // Mount parent + child admin routes
  const getAdminRoutes = () => {
    const elements = [];

    routes
      .filter((r) => r.layout === "/admin")
      .forEach((r, i) => {
        if (r.component) {
          elements.push(
            <Route key={`p-${i}`} path={r.path} element={r.component} />
          );
        }
        if (Array.isArray(r.children)) {
          r.children.forEach((c, j) => {
            elements.push(
              <Route key={`c-${i}-${j}`} path={c.path} element={c.component} />
            );
          });
        }
      });

    return elements;
  };

  document.documentElement.dir = "ltr";

  return (
    <>
      {/* Active announcements – at the very top */}
      <ActiveAnnouncementBanner />
      <div className="flex h-full w-full">
        {/* Sidebar */}
        <Sidebar open={open} onClose={() => setOpen(false)} />

        {/* Main Content */}
        <div className="h-full w-full bg-lightPrimary dark:bg-navy-900">
          <main className="mx-[2px] h-full flex-none transition-all md:pr-2 xl:ml-[270px]">
            {/* Navbar */}
            <Navbar
              onOpenSidenav={() => setOpen(true)}
              logoText="Tailwind React"
              brandText={currentRoute}
              secondary={getActiveNavbar()}
              {...props}
            />

            {/* Page Content */}
            <div className="mx-auto mb-auto h-full min-h-[calc(100vh-80px)] p-2 py-5 md:pr-2">
              <Routes>
                {getAdminRoutes()}
                <Route
                  path="/"
                  element={<Navigate to="/dashboard" replace />}
                />

                {/* Default redirect */}
                {/* <Route path="/*" element={<Navigate to="/dashboard" replace />} /> */}
              </Routes>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
